import { Button } from '@workspace/ui/components/button'
import { FormField } from '@workspace/ui/components/form-field'
import { TextInput } from '@workspace/ui/components/text-input'
import { Plus, X } from 'lucide-react'
import { useState } from 'react'

interface AddMemberFormProps {
  onAdd: (data: { name: string; email: string; position: string; linkedinUrl?: string }) => void
  isAdding?: boolean
}

export function AddMemberForm({ onAdd, isAdding }: AddMemberFormProps) {
  const [isExpanded, setIsExpanded] = useState(false)
  const [name, setName] = useState('')
  const [email, setEmail] = useState('')
  const [position, setPosition] = useState('')
  const [linkedinUrl, setLinkedinUrl] = useState('')

  const handleSubmit = () => {
    if (!name.trim() || !email.trim() || !position.trim()) return
    onAdd({
      name: name.trim(),
      email: email.trim(),
      position: position.trim(),
      linkedinUrl: linkedinUrl.trim() || undefined,
    })
    setName('')
    setEmail('')
    setPosition('')
    setLinkedinUrl('')
    setIsExpanded(false)
  }

  const handleCancel = () => {
    setName('')
    setEmail('')
    setPosition('')
    setLinkedinUrl('')
    setIsExpanded(false)
  }

  if (!isExpanded) {
    return (
      <button
        type="button"
        onClick={() => setIsExpanded(true)}
        className="w-full p-4 rounded-xl border border-dashed border-border bg-input/30 hover:border-primary/50 hover:bg-input/50 transition-all flex items-center justify-center gap-2 text-muted-foreground hover:text-white"
      >
        <Plus className="size-4" />
        Add Team Member
      </button>
    )
  }

  return (
    <div className="p-4 rounded-xl border border-primary/50 bg-input/50 space-y-4">
      <div className="flex items-center justify-between">
        <h4 className="text-sm font-medium text-white">Add Team Member</h4>
        <button
          type="button"
          onClick={handleCancel}
          className="p-1 rounded hover:bg-white/10 transition-colors"
        >
          <X className="size-4 text-muted-foreground" />
        </button>
      </div>

      <div className="space-y-3">
        <FormField label="Name" required>
          <TextInput
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Full name"
            autoFocus
          />
        </FormField>

        <div className="grid grid-cols-2 gap-3">
          <FormField label="Email" required>
            <TextInput
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="email@example.com"
              type="email"
            />
          </FormField>

          <FormField label="Role / Title" required>
            <TextInput
              value={position}
              onChange={(e) => setPosition(e.target.value)}
              placeholder="e.g., CEO, CTO"
            />
          </FormField>
        </div>

        <FormField label="LinkedIn URL">
          <TextInput
            value={linkedinUrl}
            onChange={(e) => setLinkedinUrl(e.target.value)}
            placeholder="https://linkedin.com/in/username"
          />
        </FormField>
      </div>

      <div className="flex justify-end gap-2">
        <Button variant="ghost" size="sm" onClick={handleCancel} disabled={isAdding}>
          Cancel
        </Button>
        <Button
          variant="default"
          size="sm"
          onClick={handleSubmit}
          disabled={isAdding || !name.trim() || !email.trim() || !position.trim()}
        >
          {isAdding ? 'Adding...' : 'Add Member'}
        </Button>
      </div>
    </div>
  )
}
